import type { components } from '@nutians/api-client';
/**
 * DisambiguationRow — inline clarification prompt for a pending log entity.
 *
 * Three resolution paths (ADR-0003 §5):
 *  1. Candidate button (chosenFoodId) — deterministic, no AI call.
 *  2. Free-text answer (answerText) — re-extracts with the user's words.
 *  3. Decline (decline: true) — removes the clarification without an entry.
 *
 * Keyboard: Tab cycles through controls; Enter/Space activates.
 * Screen-reader: fieldset + legend names the group.
 */
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.js';

type Clarification = components['schemas']['LogResponse']['clarifications'][number];
type LogResponse = components['schemas']['LogResponse'];

interface DisambiguationRowProps {
  rawLogId: string;
  clarification: Clarification;
  onResolved: (response: LogResponse) => void;
}

type ClarifyPayload =
  | { kind: 'food'; chosenFoodId: string }
  | { kind: 'text'; answerText: string }
  | { kind: 'decline' };

export function DisambiguationRow({ rawLogId, clarification, onResolved }: DisambiguationRowProps) {
  const { t } = useTranslation('log');
  const isConfirm = clarification.kind === 'confirm';
  const [showDescribe, setShowDescribe] = useState(false);
  const [describeText, setDescribeText] = useState('');

  const mutation = useMutation({
    mutationFn: async (payload: ClarifyPayload) => {
      const body =
        payload.kind === 'food'
          ? {
              entityName: clarification.entityName,
              chosenFoodId: payload.chosenFoodId,
              answerText: null,
              decline: false,
            }
          : payload.kind === 'text'
            ? {
                entityName: clarification.entityName,
                chosenFoodId: null,
                answerText: payload.answerText,
                decline: false,
              }
            : {
                entityName: clarification.entityName,
                chosenFoodId: null,
                answerText: null,
                decline: true,
              };

      const { data, error } = await api.POST('/me/logs/{rawLogId}/clarify', {
        params: { path: { rawLogId } },
        body,
      });
      if (error || !data) throw new Error('Clarification failed');
      return data;
    },
    onSuccess: onResolved,
  });

  function handleDescribeSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = describeText.trim();
    if (!trimmed || mutation.isPending) return;
    mutation.mutate({ kind: 'text', answerText: trimmed });
  }

  return (
    <fieldset
      className="rounded-lg border border-acorn/30 bg-acorn/10 px-4 py-3 text-sm
                 dark:border-acorn/30 dark:bg-acorn/10"
      aria-busy={mutation.isPending}
      aria-label={`Clarification: ${clarification.question}`}
    >
      <legend className="text-xs font-medium text-acorn dark:text-acorn px-1">
        {isConfirm ? t('clarification.titleConfirm') : t('clarification.title')}
      </legend>

      <p className="mb-3 text-ink dark:text-ink">{clarification.question}</p>

      {/* Candidate buttons */}
      {/* biome-ignore lint/a11y/useSemanticElements: changing to fieldset breaks the flex layout */}
      <div className="flex flex-wrap gap-2" role="group" aria-label="Choose an option">
        {clarification.options.map((option) => (
          <button
            key={option.label}
            type="button"
            disabled={mutation.isPending || option.foodId === null}
            onClick={() => {
              if (option.foodId) mutation.mutate({ kind: 'food', chosenFoodId: option.foodId });
            }}
            aria-label={`Choose: ${option.label}`}
            className="rounded-full border border-ink/20 bg-shell px-4 py-2
                       text-sm font-medium text-ink
                       hover:border-ink/40 hover:bg-shell
                       focus:outline-none focus:ring-2 focus:ring-leaf
                       disabled:opacity-50 disabled:cursor-not-allowed
                       dark:border-ink/20 dark:bg-shell
                       dark:text-ink dark:hover:bg-shell
                       min-h-[44px]"
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Free-text answer toggle */}
      {!showDescribe && (
        <button
          type="button"
          onClick={() => setShowDescribe(true)}
          disabled={mutation.isPending}
          className="mt-3 text-xs text-leaf hover:underline focus:outline-none
                     focus:underline disabled:opacity-50"
        >
          {t('clarification.describeInstead')}
        </button>
      )}

      {showDescribe && (
        <form onSubmit={handleDescribeSubmit} className="mt-3 flex flex-col gap-2">
          <input
            type="text"
            value={describeText}
            onChange={(e) => setDescribeText(e.target.value)}
            placeholder={t('clarification.describeItPlaceholder')}
            disabled={mutation.isPending}
            className="w-full rounded border border-ink/20 px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-leaf
                       disabled:opacity-50
                       dark:border-ink/20 dark:bg-shell dark:text-ink"
            aria-label={t('clarification.describeInstead')}
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={mutation.isPending || describeText.trim().length === 0}
              className="rounded px-3 py-1.5 text-xs font-medium bg-leaf text-white
                         hover:bg-leaf-deep disabled:opacity-50
                         focus:outline-none focus:ring-2 focus:ring-leaf
                         min-h-[44px] min-w-[44px]"
            >
              {t('clarification.describeItSubmit')}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowDescribe(false);
                setDescribeText('');
              }}
              disabled={mutation.isPending}
              className="rounded px-3 py-1.5 text-xs font-medium text-ink-soft
                         hover:text-ink disabled:opacity-50
                         focus:outline-none focus:ring-2 focus:ring-leaf
                         min-h-[44px] min-w-[44px]"
            >
              {t('entry.cancel')}
            </button>
          </div>
        </form>
      )}

      {/* Decline — "I didn't have this" */}
      <button
        type="button"
        onClick={() => mutation.mutate({ kind: 'decline' })}
        disabled={mutation.isPending}
        className="mt-3 block text-xs text-ink-soft hover:text-clay
                   focus:outline-none focus:underline disabled:opacity-50"
      >
        {t('clarification.noneOfThese')}
      </button>

      {mutation.isPending && (
        <p aria-live="polite" className="mt-2 text-xs text-ink-soft dark:text-ink-soft">
          {t('clarification.resolving')}
        </p>
      )}

      {mutation.isError && (
        <p role="alert" aria-live="assertive" className="mt-2 text-xs text-clay dark:text-clay">
          {t('clarification.error')}
        </p>
      )}
    </fieldset>
  );
}
